/**
 * Shared precomputation helpers used by the ramp-flag stores to turn raw
 * Apollo lists into O(1) `rampKey -> boolean` maps at hydrate time.
 */

export type FeatureFlagEntry = {
  rampKey: string;
  isEnabled: boolean;
} | null;

export type FeatureFlagList = ReadonlyArray<FeatureFlagEntry>;

export type OrgFeatureFlagsEntry = {
  id: string;
  name: string;
  featureFlags: FeatureFlagList;
};

export type FlagMap = Record<string, boolean>;

/**
 * Precomputed maps for the viewer's organizations. `perOrgId` is keyed by
 * `org.id` and used when the caller scopes to a specific org.
 * `anyOrgEnabledMap` is the "any org enables this key" boolean used by the
 * unscoped reader, and matches the legacy loop in
 * `useOrgRampFlagWithLoading` (first truthy wins; otherwise falls back to
 * the last seen value which may be `undefined` / `false`).
 */
export type OrgFlagMaps = {
  perOrgId: Record<string, FlagMap>;
  anyOrgEnabledMap: Record<string, boolean | undefined>;
};

/** Returns `null` when the list is missing so readers can distinguish unhydrated. */
export function toFlagMap(
  list: FeatureFlagList | null | undefined
): FlagMap | null {
  if (!list) {
    return null;
  }
  const out: FlagMap = {};
  for (const entry of list) {
    if (entry == null) {
      continue;
    }
    out[entry.rampKey] = entry.isEnabled;
  }
  return out;
}

/**
 * Flattens the viewer's organizations into:
 *   - `perOrgId`: `orgId -> rampKey -> boolean` (for orgId-scoped lookups),
 *   - `anyOrgEnabledMap`: `rampKey -> boolean | undefined` (true if any org enables the
 *     key; otherwise the last seen value, to preserve the legacy loop's
 *     "undefined means no entry" vs "false means explicitly disabled" signal).
 * Returns `null` when the list is missing so readers can distinguish
 * unhydrated from empty.
 */
export function toOrgFlagMaps(
  orgs: ReadonlyArray<OrgFeatureFlagsEntry> | null | undefined
): OrgFlagMaps | null {
  if (!orgs) {
    return null;
  }
  const perOrgId: Record<string, FlagMap> = {};
  const allKeys = new Set<string>();
  for (const org of orgs) {
    const map = toFlagMap(org.featureFlags) ?? {};
    perOrgId[org.id] = map;
    for (const key of Object.keys(map)) {
      allKeys.add(key);
    }
  }

  // Replays the legacy loop in `useOrgRampFlagWithLoading`: first truthy org
  // wins, otherwise fall through to the last seen value - which may be
  // `undefined` when the last org doesn't define the key - so callers can
  // still distinguish "explicitly disabled" from "no entry".
  const anyOrgEnabledMap: Record<string, boolean | undefined> = {};
  for (const rampKey of allKeys) {
    let anyEnabled = false;
    let lastValue: boolean | undefined;
    for (const org of orgs) {
      const val = perOrgId[org.id]?.[rampKey];
      lastValue = val;
      if (val) {
        anyEnabled = true;
        break;
      }
    }
    anyOrgEnabledMap[rampKey] = anyEnabled ? true : lastValue;
  }

  return {perOrgId, anyOrgEnabledMap};
}
